import close from 'images/icons/orders/close.svg';
import product from 'images/icon/systemSettings/product.svg';
import * as React from 'react';
import { CategoryAndProducts, getProductPerCategories } from './action';
import styled from 'styled-components';
import { BorderColor2, SurfaceColor } from '../../../styles/global/css/Utils';
import Popup from '../../popup/Popup';
import { PopupMode } from '../../popup/model/Model';
import { Collapse } from 'reactstrap';
import { Open } from '../../../styles/global/css/Open';
import { Translate, translateToString } from '../../../styles/global/translate';
import { useRecoilState } from 'recoil';
import { AProductFilter, ProductAtomType } from '../../../atoms/filter/productsFilterAtom';
import { FlexDiv } from '../../../containers_v2/products/style';
import { GenericToolbarFilterProps } from '../ToolbarFilter';
import { ReportFiltersContext } from '../../../containers_v2/reports/generic/generic';
import { BoolCheckbox } from '../../../containers_v2/forms/PopupField';

const Container = styled.div<{ fullScreen?: boolean }>`
	${({ fullScreen }) => fullScreen ? '' : 'width: 180px'};
	padding: 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: 1px solid ${BorderColor2};
	width: 100%;
	height: 28px;
	border-radius: 5px;
	cursor: pointer;
	font-size: 12px;
`;

const Logo = styled.img`
	height: 18px;
	padding: 0 7px;
`;

const Text = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	white-space: nowrap;
`;

const Arrow = styled.i`
	margin-right: 4px;
	padding: 0 10px;
	width: 10%;
	color: hsl(0,0%,80%);
`;

function getProductsId(category: CategoryAndProducts, uncheck?: boolean): number[] {
	return [...category.products.reduce((acc, product) => product.checked || uncheck ? [...acc, product.id] : acc, []), ...category.children.reduce((acc, category) => [...acc, ...getProductsId(category, uncheck)], [])];
}

function setProductsInCategory(category: CategoryAndProducts, products: number[]): void {
	category.products.forEach(product => product.checked = products.includes(product.id));
	category.children.forEach(category => setProductsInCategory(category, products));
}

export default function ProductPerCategoryFilter(props: GenericToolbarFilterProps & { atomId: number, max?: number }) {
	const { max } = props;
	const [categories, setCategories] = React.useState<CategoryAndProducts[]>([]);
	const [isOpen, setIsOpen] = React.useState(false);
	const count = categories.reduce((acc, category) => acc + getProductsId(category).length, 0);
	const [_filteredProducts, setFilteredProducts] = useRecoilState(AProductFilter);
	const { filters, setFilters } = React.useContext(ReportFiltersContext);

	let filteredProducts: ProductAtomType;

	if (props.context) {
		if (filters.my_filters) {
			filteredProducts = filters.my_filters.products;
		}
	} else {
		filteredProducts = _filteredProducts;
	}

	React.useEffect(() => {
		getProductPerCategories().then(categories => {
			if (max === undefined) {
				categories.forEach(category => {
					if (props.context) {
						setProductsInCategory(category, filteredProducts?.products ?? []);
					} else {
						setCategory(category, true);
					}
				});
			}
			if (!filteredProducts && !props.context) {
				const ids = categories.reduce((acc, category) => [...acc, ...getProductsId(category)], []);
				if (props.context) {
					setFilters({ ...filters, my_filters: { ...filters.my_filters, products: { products: ids, all: true } } });
				} else {
					setFilteredProducts({ products: ids, all: true });
				}
			}
			setCategories(categories);
		});
	}, []);

	React.useEffect(() => {
		if (filteredProducts) {
			categories.forEach(category => category.products.forEach(product => product.checked = filteredProducts?.products.includes(product.id) ?? false));
			setCategories([...categories]);
		}
		else {
			getProductPerCategories().then(categories => {
				if (max === undefined) {
					categories.forEach(category => setCategory(category, true));
				}
				if (!filteredProducts) {
					const ids = categories.reduce((acc, category) => [...acc, ...getProductsId(category)], []);
					if (props.context) {
						setFilters(filters => ({ ...filters, my_filters: { ...(filters?.my_filters ?? {}), products: { products: ids, all:  true } } }));
					} else {
						setFilteredProducts({ products: ids, all: true });
					}
				}
				setCategories(categories);

			});
		}
	}, [filteredProducts]);

	if (props.hidden) return <></>;

	return <>
		<Container onClick={() => setIsOpen(true)} fullScreen={props.fullScreen}>
			<Logo src={product} />
			{!filteredProducts?.all && <Text>{count} {translateToString('selected')}</Text>}
			{filteredProducts?.all && <Text> {translateToString('report_filters.all_products_selected')}</Text>}
			<Arrow className="fas fa-caret-down" />
		</Container>
		<Popup isOpen={isOpen} onClickOut={() => setIsOpen(false)} popupMode={PopupMode.Centered} popupStyle={{ fitContent: true }}>
			<ProductPerCategoryPopup
				products={categories}
				setAll={() => {
					if (props.context) {
						if (filteredProducts) {
							filteredProducts.all = !filteredProducts.all;
							filteredProducts && setFilters(filters => ({ ...filters, my_filters: { ...(filters?.my_filters ?? {}), products: filteredProducts } }));
						}
					}
					else {
						filteredProducts && setFilteredProducts({ ...filteredProducts, all: !filteredProducts.all });
					}
				}}
				all={filteredProducts?.all ?? false}
				setProducts={categories => {
					const ids = categories.reduce((acc, category) => [...acc, ...getProductsId(category)], []);
					if (props.context) {
						setFilters(filters => ({ ...filters, my_filters: { ...(filters?.my_filters ?? {}), products: { products: ids, all: false } } }));
					} else {
						setFilteredProducts({ products: ids, all: false });
					}
					setCategories(categories);
				}}
				onClose={() => setIsOpen(false)}
				max={max}
			/>
		</Popup>
	</>;
}

const CategoryTitle = styled.h4`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 0px;
	padding-bottom: 1px;
	position: sticky;
	z-index: 1;
	top: 0;
	background: ${SurfaceColor};
	padding-left: 44px;
	margin-left: -44px;
	font-size: 12px;
	font-weight: 500;
`;

const DepthPadder = styled.div<{ depth: number }>`
	padding-left: 44px;
	${p => p.depth === 1 ? '' : 'padding-bottom: 5px;'}
`;

const Product = styled.div`
	font-size: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

function isCategoryActive(category: CategoryAndProducts): boolean {
	return category.products.every(p => p.checked) && category.children.every(isCategoryActive);
}

function isCategoryInactive(category: CategoryAndProducts): boolean {
	return category.products.every(p => !p.checked) && category.children.every(isCategoryInactive);
}

function isCategoryPartiallyActive(category: CategoryAndProducts): boolean {
	return !isCategoryActive(category) && !isCategoryInactive(category);
}

function setCategory(category: CategoryAndProducts, value: boolean): void {
	category.products.forEach(p => p.checked = value);
	category.children.forEach(c => setCategory(c, value));
}

function hasCategoryProducts(category: CategoryAndProducts): boolean {
	return category.products.length !== 0 || category.children.some(hasCategoryProducts);
}

interface ThreeStateCheckboxProps {
	checked: boolean;
	indeterminate: boolean;
	onClick: () => void;
	disabled?: boolean;
	width?: string;
	type: 'checkbox';
}

function ThreeStateCheckbox({ checked, indeterminate, onClick, disabled, width, type }: ThreeStateCheckboxProps) {
	const checkboxRef = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		if (checkboxRef.current) {
			checkboxRef.current.indeterminate = indeterminate;
		}
	}, [indeterminate]);

	return (
		<input
			ref={checkboxRef}
			checked={checked}
			style={{
				margin: 0,
				accentColor: 'hsl(198, 100%, 44%)',
				cursor: 'pointer',
				filter: 'brightness(106%)',
				width: width
			}}
			type={type}
			onClick={onClick}
			disabled={disabled}
		/>
	);
}

function RecursiveCategoryDisplayer(props: {
	category: CategoryAndProducts,
	depth: number,
	setProducts: () => void,
	max: number | undefined,
	disabled: boolean,
}) {
	const [isOpen, setIsOpen] = React.useState(false);
	const isActive = isCategoryActive(props.category);
	const isPartiallyActive = isCategoryPartiallyActive(props.category);
	const count = getProductsId(props.category, true).length;

	if (!hasCategoryProducts(props.category)) return <></>;
	return <>
		<CategoryTitle>
			<Open isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} width={12} height={8} />
			<ThreeStateCheckbox
				checked={isActive}
				indeterminate={isPartiallyActive}
				width='13px'
				type='checkbox'
				onClick={() => {
					if (props.disabled) return;
					if (!isActive && props.max !== undefined && props.max - count < 0) return;
					setCategory(props.category, !isActive);
					props.setProducts();
				}}
				disabled={props.disabled}
			/>
			{props.category.name ?? translateToString('no_category')}
		</CategoryTitle>
		<DepthPadder depth={props.depth}>
			<Collapse isOpen={isOpen}>
				{props.category.products.length === 0 && props.category.children.length === 0 && <Product>{translateToString('no_products')}</Product>}
				{props.category.products.map(p => <Product key={`Product[${p.id}]`}>
					<BoolCheckbox 
						checked={p.checked ?? false}
						onClick={() => {
							if (props.disabled) return;
							if (!(p.checked ?? false) && props.max !== undefined && props.max - 1 < 0) return;
							p.checked = !(p.checked ?? false);
							props.setProducts();
						}}
						width='12px'
						type='checkbox'
						disabled={props.disabled}
					/>
					{p.name}
				</Product>)}
				{props.category.children.map(p => <RecursiveCategoryDisplayer
					key={`RecursiveCategory[${p.id ?? 'no-category'}][${props.depth}]`}
					category={p}
					depth={props.depth + 1}
					setProducts={props.setProducts}
					max={props.max}
					disabled={props.disabled}
				/>)}
			</Collapse>
		</DepthPadder>
	</>;
}

const PopupContainer = styled.div`
	margin: 15px;
	max-height: 75vh;
	display: flex;
	gap: 10px;
	flex-flow: column;
`;

const CategoriesContainer = styled.div`
	display: flex;
	gap: 5px;
	flex-flow: column;
	overflow: auto;
	align-items: flex-start;
`;

const PopupTitle = styled.h4`
	justify-content: space-between;
	display: flex;
	align-items: center;
	border-bottom: 1px solid ${BorderColor2};
	margin-bottom: 5px;
	padding-bottom: 5px;
`;

const Close = styled.img`
	cursor: pointer;
	height: 25px;
`;

function ProductPerCategoryPopup(props: {
	products: CategoryAndProducts[],
	setProducts: (products: CategoryAndProducts[]) => void,
	onClose: () => void,
	max: number | undefined,
	setAll: () => void,
	all: boolean
}) {
	const count = props.products.reduce((acc, category) => acc + getProductsId(category).length, 0);
	const allCategoriesActive = props.products.every(c => isCategoryActive(c));
	const noCategoriesActive = props.products.every(c => isCategoryInactive(c));

	React.useEffect(() => {
		if (allCategoriesActive && !props.all) {
			props.setAll();
		} else if (!allCategoriesActive && props.all) {
			props.setAll();
		}
	}, [allCategoriesActive, props.all, props.setAll]);

	return (
		<PopupContainer>
			<PopupTitle>
				<Translate id='categories' />
				<Close src={close} onClick={props.onClose} />
			</PopupTitle>
			{props.max !== undefined && <FlexDiv gap='10px'>
				<Product>
					{count} / {props.max}
				</Product>
			</FlexDiv>}
			<CategoriesContainer>
				<FlexDiv gap='10px' fontSize='12px' fontWeight='500'>
					<ThreeStateCheckbox
						checked={allCategoriesActive}
						indeterminate={props.products.some(c => isCategoryActive(c) || isCategoryPartiallyActive(c)) && !noCategoriesActive && !allCategoriesActive}
						width='13px'
						type='checkbox'
						onClick={() => {
							props.products.forEach(category => setCategory(category, !allCategoriesActive));
							props.setProducts([...props.products]);
						}}
					/>
					<Translate id='all' />
				</FlexDiv>
				{props.products.map(p => <RecursiveCategoryDisplayer
					key={`RecursiveCategory[${p.id ?? 'no-category'}][0]`}
					category={p}
					depth={1}
					setProducts={() => props.setProducts([...props.products])}
					max={props.max !== undefined ? props.max - count : undefined}
					disabled={false}
				/>)}
			</CategoriesContainer>
		</PopupContainer>
	);
}
